import React from 'react'

import { referencePriceDecimals } from '../../../foundations/price-decimals'
import { roundHalfAwayFromZeroAt } from '../../../foundations/producer-rounding'
import { formatKpiFigure } from '../../../plots/kpi/formatKpiFigure'
import { kpiDeckTileAdapter } from '../../../plots/kpi/readAdapters'
import { TextClamp } from '../../text/TextClamp'

import { PlotFigure } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * The exec-summary KPI tile, extracted so a second slide can render the same read.
 *
 * It was written inline in `ExecSummarySlide.tsx` for the strip
 * (docs/plans/2026-09-17-exec-summary-kpi-strip-design-memo.md A1/A4/A5). The rating
 * slide now carries one KPI of its own, and the whole point of that decision is that
 * the two surfaces cannot print the same statistic differently -- which a second copy
 * of this formatter would immediately allow. So the formatting, the
 * value-already-in-the-sentence rule and the tile itself live here once, and both
 * slides call them.
 *
 * The tile takes a SUBSET of the exec-summary `kpis[]` item, never the whole thing.
 * Base fields stay optional so the rating slide's narrower `kpi` contract can use the
 * same renderer, while routed executive reads can state their actual denominator.
 */

export type KpiValueFormat = 'percent' | 'count' | 'points' | 'currency'

/** What the tile needs: `Pick<ExecSummaryKpi, 'value' | 'valueFormat' | 'unit'>` plus a sentence. */
export interface KpiTileRead {
  value: number
  valueFormat: KpiValueFormat
  unit: string
  sentence: string
  /**
   * Declared display precision for this read (`MetricDisplaySpec.decimals`).
   * Display-only: the stored `value` is never rounded. Absent on decks stamped
   * before the field, which keep the previous two-decimal currency spelling.
   */
  decimals?: number | null
}

/** Format from `value` + `valueFormat` + `unit`. Percentage points always render `pp`, never `%` -- the schema's `unit` pattern also forbids mixing them. */
export function formatKpiNumber(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) {
    return '0'
  }
  // A producer figure keeps the producer's rounding rule (Python round, half
  // to even), not toFixed's tie-up.
  return Number.isInteger(value) ? value.toFixed(0) : roundHalfAwayFromZeroAt(value, decimals).toFixed(decimals)
}

/** Decks carry no locale today; `en` is what every current payload renders as. */
export const KPI_FALLBACK_LOCALE = 'en'

/**
 * The deck's locale, when the payload happens to carry one. No slide contract declares
 * `locale` yet (this is deliberately not a schema field), so it is read defensively off
 * the props rather than typed onto a slide's data: the day a deck starts carrying one,
 * currency symbols follow it with no further change here.
 */
export function kpiLocale(props: object): string {
  const raw = (props as { locale?: unknown }).locale
  return typeof raw === 'string' && raw.trim() ? raw.trim() : KPI_FALLBACK_LOCALE
}

/** Two places was the tile's own rule before a read carried declared precision. */
const KPI_CURRENCY_FALLBACK_FRACTION_DIGITS = 2

export function formatKpiGroupedNumber(
  value: number,
  locale: string,
  fractionDigits: number = KPI_CURRENCY_FALLBACK_FRACTION_DIGITS,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)
  } catch {
    return value.toFixed(fractionDigits)
  }
}

/**
 * Currency reads print the SYMBOL, not the ISO code: "€363.46" and "$1,240.00", which is
 * how the figure reaches the reader everywhere else in the deck. `unit` carries the code
 * (the schema's pattern is `[A-Z]{3}`), and `Intl` maps it to the symbol for the deck's
 * locale.
 *
 * Fraction digits follow the read's declared `decimals` when it is a usable count;
 * otherwise the previous two-place spelling stands. Display rounding never writes back
 * onto the stored `value`.
 *
 * `Intl` throws a `RangeError` on a malformed currency code or locale, and a slide must
 * render whatever the payload holds, so anything it rejects falls back to the previous
 * `CODE 363.46` spelling rather than breaking the deck.
 */
export function formatKpiCurrency(
  value: number,
  unit: string,
  locale: string,
  decimals?: number | null,
): string {
  const fractionDigits = referencePriceDecimals(
    decimals,
    KPI_CURRENCY_FALLBACK_FRACTION_DIGITS,
  )
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: unit,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)
  } catch {
    return `${unit} ${formatKpiGroupedNumber(value, locale, fractionDigits)}`
  }
}

export function formatKpiValue(
  kpi: Pick<KpiTileRead, 'value' | 'valueFormat' | 'unit' | 'decimals'>,
  locale: string = KPI_FALLBACK_LOCALE,
): string {
  const { value, valueFormat, unit, decimals } = kpi
  if (valueFormat === 'percent') {
    return `${formatKpiNumber(value)}%`
  }
  if (valueFormat === 'points') {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${formatKpiNumber(value)} pp`
  }
  if (valueFormat === 'currency') {
    return formatKpiCurrency(value, unit, locale, decimals)
  }
  // count
  return `n ${formatKpiNumber(value)}`
}

/**
 * The tile's printed figure. A tile the deck producer stamped with its
 * `decimals` (plots plan F) prints through the kpi plot's one formatter
 * (`formatKpiFigure`), read by `kpiDeckTileAdapter`; a count keeps the tile's
 * own `n` prefix. A stored tile without the stamp keeps `formatKpiValue`.
 */
export function kpiTileValue(
  kpi: Pick<KpiTileRead, 'value' | 'valueFormat' | 'unit' | 'decimals' | 'sentence'>,
  locale: string = KPI_FALLBACK_LOCALE,
): string {
  if (kpiDeckTileAdapter.accepts(kpi)) {
    const printed = kpiFigureTileValue(kpiDeckTileAdapter.read(kpi).data.items[0]?.figure, locale)
    if (printed != null) {
      return printed
    }
  }
  return formatKpiValue(kpi, locale)
}

/**
 * A stamped kpi figure as the tile prints it (plots wave 7, D4): the kpi
 * plot's one formatter, with the tile's own `n` prefix on a count. Null when
 * the figure carries no value (the caller keeps its stored reader).
 */
export function kpiFigureTileValue(figure: PlotFigure | null | undefined, locale: string = KPI_FALLBACK_LOCALE): string | null {
  const printed = figure ? formatKpiFigure(figure, { locale }) : null
  if (printed == null || !figure) {
    return null
  }
  return figure.format === 'count' ? `n ${printed}` : printed
}

/**
 * Copy written before the strip's continuation contract: a whole sentence that already
 * opens with the figure ("8% of respondents rated it highly", "€363.5 is the median
 * spend"). Those decks are already delivered, and printing the value line above such a
 * sentence says the number twice, so the tile renders the sentence on its own.
 *
 * Keyed on the shape of the string only: a leading signed digit, or a leading currency
 * sign (ASCII `$`, Latin-1 `£` / `¥`, or anything in the Currency Symbols block, which is
 * where `€` lives). Never on a study, a question or a value. New payloads open with a
 * word, so this never fires on them.
 */
const KPI_SENTENCE_OPENS_WITH_FIGURE = /^(?:[-+]?\d|[$£¥₠-₿])/

export function kpiSentenceCarriesItsFigure(sentence: unknown): boolean {
  return typeof sentence === 'string' && KPI_SENTENCE_OPENS_WITH_FIGURE.test(sentence.trim())
}

/** Structurally what `visibleTextClampFromSchema` returns, so a schema-backed clamp passes straight in. */
export interface KpiTileClamp {
  minLines: number
  maxLines: number
  lineHeight?: string | number
}

export interface KpiTileProps {
  /** Existing evidence hosts keep complete text in their natural flow. */
  completeText?: boolean
  kpi: KpiTileRead
  clamp: KpiTileClamp
  locale?: string
  testId: string
  valueTestId: string
  copyTestId: string
  sentenceTestId: string
  /**
   * Ink for the numeral. The exec-summary strip draws it in the deck's data accent;
   * a slide whose own figures are already in ink passes ink, so the tile does not
   * introduce a second emphasis colour beside them.
   */
  valueColor?: string
  /**
   * The figure already printed by the kpi plot's one formatter
   * (`plots/kpi/formatKpiFigure`), for a payload Python stamped with its
   * decimals. Absent on stored tiles, which keep `formatKpiValue` (legacy).
   */
  formattedValue?: string
  /**
   * Low-base note (`labels.plot_low_base`) when Python stamped this tile
   * `lowBase`. Absent draws nothing.
   */
  lowBaseNote?: string
}

/**
 * The value sits on its own line with the copy stacked tightly under it, both the full
 * tile width, so the number's digit count cannot shift the layout.
 */
export function KpiTile(props: KpiTileProps) {
  const { kpi, clamp, lowBaseNote } = props
  const carriesFigure = kpiSentenceCarriesItsFigure(kpi.sentence)
  const valueLine = carriesFigure ? null : (
    <TextClamp
      data-testid={props.valueTestId}
      minLines={1}
      maxLines={props.completeText ? undefined : 1}
      lineHeight={clamp.lineHeight}
      style={{
        fontFamily: 'var(--ds-font-sans)',
        fontSize: 'var(--ds-fs-cover-subtitle)',
        fontWeight: 'var(--ds-weight-semibold)',
        color: props.valueColor || 'var(--ds-exec-chip-fg)',
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {props.formattedValue ?? kpiTileValue(kpi, props.locale)}
    </TextClamp>
  )

  const lowBaseNoteLine = lowBaseNote ? (
    <span
      data-testid={`${props.testId}-low-base`}
      style={{
        fontFamily: 'var(--ds-font-sans)',
        fontSize: 'var(--ds-fs-caption)',
        fontWeight: 'var(--ds-weight-regular)',
        color: 'var(--ds-text-muted)',
      }}
    >
      {lowBaseNote}
    </span>
  ) : null

  return (
    <div
      data-testid={props.testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--ds-gap-xs)',
        minWidth: 0,
      }}
    >
      {valueLine}
      <TextClamp
        data-testid={props.copyTestId}
        minLines={clamp.minLines}
        maxLines={props.completeText ? undefined : clamp.maxLines}
        lineHeight={clamp.lineHeight}
        style={{
          fontFamily: 'var(--ds-font-sans)',
          fontSize: 'var(--ds-fs-body-sm)',
          fontWeight: 'var(--ds-weight-regular)',
          color: 'var(--ds-text-primary)',
        }}
      >
        <span data-testid={props.sentenceTestId}>{kpi.sentence}</span>
      </TextClamp>
      {lowBaseNoteLine}
    </div>
  )
}
